/**
 * guards — runtime narrowing for untyped wire payloads.
 *
 * Everything the backend sends arrives as `unknown`: the transport validates the
 * ENVELOPE only and leaves `data` opaque on purpose, so each domain interpreter
 * and error parser has to prove a field's shape before reading it. These are the
 * proofs, in one place.
 *
 * They were duplicated four ways before this file existed — `asRecord` verbatim
 * in two domain interpreters, `asBag` doing the same job under another name in
 * the error parser, plus a local `str`. Same job, four names, and each copy free
 * to drift.
 *
 * Every guard returns `undefined` rather than throwing or coercing: a malformed
 * field must degrade one value, never kill the frame that carried it.
 */

/** A plain object, EXCLUDING arrays — `data`, `artifact`, `tool_info`, an error body. */
export function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

/** A non-empty string. Empty is treated as absent — the wire uses `""` for "unset". */
export function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/** A non-empty array. */
export function asArray(value: unknown): unknown[] | undefined {
  return Array.isArray(value) && value.length > 0 ? value : undefined;
}
